// Date helpers. Dates are YYYY-MM-DD strings; months are YYYY-MM-01. "Today" is in the org timezone.

const pad = (n: number) => String(n).padStart(2, "0");

export function isoDate(y: number, m: number, d: number): string {
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function todayIn(tz: string | undefined): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    return `${g("year")}-${g("month")}-${g("day")}`;
  } catch {
    const d = new Date();
    return isoDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
  }
}

export function monthOf(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

export function addMonths(month: string, n: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + n;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  return isoDate(yy, mm + 1, 1);
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function monthLabel(month: string, short = false): string {
  const m = Number(month.slice(5, 7)) - 1;
  const name = MONTHS[m] ?? "";
  return `${short ? name.slice(0, 3) : name} ${month.slice(0, 4)}`;
}

export function monthShort(month: string): string {
  return (MONTHS[Number(month.slice(5, 7)) - 1] ?? "").slice(0, 3);
}

function parts(date: string): [number, number, number] {
  return [Number(date.slice(0, 4)), Number(date.slice(5, 7)), Number(date.slice(8, 10))];
}

function toUtc(date: string): number {
  const [y, m, d] = parts(date);
  return Date.UTC(y, m - 1, d);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / 86_400_000);
}

export function addDays(date: string, n: number): string {
  const t = new Date(toUtc(date) + n * 86_400_000);
  return isoDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Grid display: 10/17 (this year) or 10/17/27. */
export function dateShort(date: string | null | undefined, today?: string): string {
  if (!date) return "";
  const [y, m, d] = parts(date);
  const thisYear = today ? Number(today.slice(0, 4)) : new Date().getFullYear();
  return y === thisYear ? `${m}/${d}` : `${m}/${d}/${String(y).slice(2)}`;
}

export function dateLong(date: string | null | undefined): string {
  if (!date) return "";
  const [y, m, d] = parts(date);
  const wd = WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd}, ${MONTHS[m - 1].slice(0, 3)} ${d} ${y}`;
}

export function relativeDays(date: string, today: string): string {
  const n = daysBetween(today, date);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  return n > 0 ? `in ${n}d` : `${-n}d ago`;
}

export type DateParse = { ok: true; value: string | null } | { ok: false; message: string };

function valid(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/**
 * Smart date input (§5.5): 2026-10-17 · 10/17 · 10/17/26 · 10/17/2026 · today · tom · +3 · +2w ·
 * eom · tbd/blank → null · Excel serial numbers (46312) from pasted cells.
 */
export function parseDate(input: string, today: string): DateParse {
  const s = input.trim().toLowerCase();
  if (s === "" || s === "tbd" || s === "-" || s === "—" || s === "n/a") return { ok: true, value: null };
  if (s === "today" || s === "tod") return { ok: true, value: today };
  if (s === "tom" || s === "tomorrow") return { ok: true, value: addDays(today, 1) };
  if (s === "eom") {
    const next = addMonths(monthOf(today), 1);
    return { ok: true, value: addDays(next, -1) };
  }
  let m = /^\+(\d{1,3})(d|w)?$/.exec(s);
  if (m) return { ok: true, value: addDays(today, Number(m[1]) * (m[2] === "w" ? 7 : 1)) };
  m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:t.*)?$/.exec(s);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return valid(y, mo, d) ? { ok: true, value: isoDate(y, mo, d) } : { ok: false, message: `${input} isn't a real date` };
  }
  m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/.exec(s);
  if (m) {
    const mo = Number(m[1]);
    const d = Number(m[2]);
    let y: number;
    if (m[3]) {
      y = Number(m[3]);
      if (y < 100) y += 2000;
    } else {
      y = Number(today.slice(0, 4));
      if (valid(y, mo, d) && daysBetween(today, isoDate(y, mo, d)) < -60) y += 1;
    }
    return valid(y, mo, d) ? { ok: true, value: isoDate(y, mo, d) } : { ok: false, message: `${input} isn't a real date` };
  }
  m = /^(\d{5})(?:\.\d+)?$/.exec(s);
  if (m) {
    // Excel serial date (1900 system).
    const serial = Number(m[1]);
    if (serial > 30000 && serial < 80000) {
      const t = new Date(Date.UTC(1899, 11, 30) + serial * 86_400_000);
      return { ok: true, value: isoDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()) };
    }
  }
  return { ok: false, message: `Couldn't read "${input}" as a date. Try 10/17 or 2026-10-17.` };
}

/** Parse a month like "Nov", "11/2026", "2026-11" → YYYY-MM-01. */
export function parseMonth(input: string, today: string): DateParse {
  const s = input.trim().toLowerCase();
  if (s === "") return { ok: true, value: null };
  let m = /^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/.exec(s);
  if (m) return { ok: true, value: isoDate(Number(m[1]), Number(m[2]), 1) };
  m = /^(\d{1,2})\/(\d{2}|\d{4})$/.exec(s);
  if (m) {
    let y = Number(m[2]);
    if (y < 100) y += 2000;
    return { ok: true, value: isoDate(y, Number(m[1]), 1) };
  }
  const idx = MONTHS.findIndex((n) => s.startsWith(n.slice(0, 3).toLowerCase()));
  if (idx >= 0) {
    let y = Number(today.slice(0, 4));
    if (idx + 1 < Number(today.slice(5, 7))) y += 1;
    return { ok: true, value: isoDate(y, idx + 1, 1) };
  }
  const d = parseDate(input, today);
  if (d.ok && d.value) return { ok: true, value: monthOf(d.value) };
  return { ok: false, message: `Couldn't read "${input}" as a month` };
}
